import {
  CloseOutlined,
  DeleteOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import { useModel } from '@umijs/max';
import {
  Badge,
  Checkbox,
  Modal,
  message,
  Progress,
  Table,
  Tabs,
  Typography,
} from 'antd';
import { createStyles } from 'antd-style';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { TaskDirection, TaskItem } from '@/models/taskCenter';

const { Text } = Typography;

const formatSize = (bytes: number, maxUnit: number = 4): string => {
  if (bytes <= 0) return '0B';
  const units = ['B', 'KB', 'M', 'GB', 'TB'];
  let i = 0;
  let val = bytes;
  while (val >= 1024 && i < Math.min(maxUnit, units.length - 1)) {
    val /= 1024;
    i++;
  }
  return val < 10
    ? `${val.toFixed(1)}${units[i]}`
    : `${Math.round(val)}${units[i]}`;
};

const formatSpeed = (bytesPerSec: number): string => {
  return `${formatSize(bytesPerSec)}/S`;
};

const BLUE = '#00A3DD';
const TASK_PAGE_SIZE_DEFAULT = 5;
const TASK_CENTER_TOP_OFFSET = 56;

const useStyles = createStyles(() => ({
  modal: {
    position: 'fixed !important' as any,
    top: `${TASK_CENTER_TOP_OFFSET}px !important`,
    right: '20px !important',
    left: 'auto !important',
    margin: '0 !important',
    padding: '0 !important',
    '.ant-modal': {
      top: 0,
      margin: 0,
      padding: 0,
      maxWidth: 'none',
    },
    '.ant-modal-content': {
      padding: 0,
      height: 610,
      borderRadius: 4,
      overflow: 'hidden',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
    },
    '.ant-modal-header': {
      padding: '16px 20px',
      borderBottom: '1px solid #f0f0f0',
      marginBottom: 0,
      background: '#fff',
    },
    '.ant-modal-body': {
      padding: 0,
      flex: 1,
      minHeight: 0,
    },
    '.ant-modal-wrap': {
      position: 'fixed',
    },
    '.ant-checkbox-checked .ant-checkbox-inner': {
      backgroundColor: `${BLUE} !important`,
      borderColor: `${BLUE} !important`,
    },
    '.ant-checkbox-indeterminate .ant-checkbox-inner::after': {
      backgroundColor: `${BLUE} !important`,
    },
  },
  container: {
    display: 'flex',
    height: '100%',
    minHeight: 0,
  },
  sidebar: {
    width: 160,
    borderRight: '1px solid #f0f0f0',
    flexShrink: 0,
    padding: 20,
    background: '#fff',
  },
  sidebarItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '9px 12px 9px 16px',
    cursor: 'pointer',
    fontSize: 14,
    color: 'rgba(0,0,0,0.65)',
    transition: 'all 0.2s',
    borderRadius: 4,
    marginBottom: 8,
    '&:hover': {
      color: BLUE,
      background: '#f5f5f5',
    },
  },
  sidebarItemActive: {
    background: '#E6F7FF',
    color: BLUE,
    fontWeight: 500,
    '&:hover': {
      background: '#E6F7FF',
    },
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    minHeight: 0,
    background: '#fff',
    paddingLeft: 20,
    paddingRight: 20,
  },
  topRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    paddingTop: 24,
    gap: 20,
  },
  toolbar: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
  },
  toolBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    width: 110,
    gap: 8,
    padding: '4px 12px',
    border: '1px solid #E6F7FF',
    borderRadius: 4,
    background: '#E6F7FF',
    cursor: 'pointer',
    fontSize: 14,
    color: BLUE,
    transition: 'all 0.2s',
    '&:hover': {
      opacity: 0.8,
    },
  },
  statusTabs: {
    '.ant-tabs-nav': {
      marginBottom: 0,
    },
    '.ant-tabs-tab': {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '22px',
      padding: '8px 0',
      color: '#999999',
      '&:hover': {
        color: BLUE,
      },
    },
    '.ant-tabs-tab-active .ant-tabs-tab-btn': {
      color: '#333333 !important',
    },
    '.ant-tabs-ink-bar': {
      background: `${BLUE} !important`,
    },
  },
  tableWrapper: {
    width: 685,
    marginTop: 16,
    paddingBottom: 0,
    background: '#fff',
    '& .ant-table': {
      background: 'transparent',
      borderRadius: '0 !important',
    },
    '& .ant-table-container': {
      borderRadius: '0 !important',
    },
    '& .ant-table-thead > tr > th': {
      background: '#fafafa',
      fontWeight: 600,
      color: 'rgba(31, 31, 31, 0.45)',
      fontSize: 14,
      height: 54,
      padding: '0 12px',
      borderBottom: 'none',
      borderRadius: '0 !important',
      '&:first-child': {
        borderTopLeftRadius: '4px !important',
      },
      '&:last-child': {
        borderTopRightRadius: '4px !important',
      },
    },
    '& .ant-table-tbody > tr > td': {
      padding: '14px 12px',
      fontSize: 14,
      fontWeight: 400,
      minHeight: 54,
      borderBottom: '1px solid #f0f0f0',
    },
    '& .ant-table-tbody > tr:hover > td': {
      background: '#fafafa',
    },
    '& .ant-table-wrapper .ant-table-pagination.ant-pagination': {
      margin: '20px 0',
    },
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingRight: 5,
  },
  progressWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    width: 120,
    flexShrink: 0,
  },
  speedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    lineHeight: '20px',
    marginTop: 2,
  },
  actionBtns: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  iconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    borderRadius: '50%',
    cursor: 'pointer',
    color: BLUE,
    fontSize: 20,
    transition: 'all 0.2s',
    '&:hover': {
      opacity: 0.7,
    },
  },
  fileIcon: {
    width: 14,
    height: 16,
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fa8c16',
    color: '#fff',
    fontSize: 10,
    flexShrink: 0,
  },
}));

interface TaskCenterModalProps {
  open: boolean;
  onClose: () => void;
}

const TaskCenterModal: React.FC<TaskCenterModalProps> = ({ open, onClose }) => {
  const { styles, cx } = useStyles();
  const { tasks, uploadCount, downloadCount, deleteTasks } =
    useModel('taskCenter');

  const [activeDirection, setActiveDirection] =
    useState<TaskDirection>('upload');
  const [activeStatus, setActiveStatus] = useState<'in-progress' | 'completed'>(
    'in-progress',
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const directionTasks = useMemo(
    () => tasks.filter((t: TaskItem) => t.direction === activeDirection),
    [activeDirection, tasks],
  );

  const inProgressTasks = useMemo(
    () => directionTasks.filter((t) => t.status === 'uploading'),
    [directionTasks],
  );
  const completedTasks = useMemo(
    () => directionTasks.filter((t) => t.status !== 'uploading'),
    [directionTasks],
  );

  const currentTasks =
    activeStatus === 'in-progress' ? inProgressTasks : completedTasks;

  useEffect(() => {
    const currentTaskIds = new Set(currentTasks.map((task) => task.id));
    setSelectedIds((prev) => prev.filter((id) => currentTaskIds.has(id)));
  }, [currentTasks]);

  const deleteBatch = useCallback(() => {
    if (selectedIds.length === 0) {
      message.warning('请先选择要删除的任务');
      return;
    }
    deleteTasks(selectedIds);
    setSelectedIds([]);
  }, [deleteTasks, selectedIds]);

  const currentTaskIds = new Set(currentTasks.map((task) => task.id));
  const selectedCurrentCount = selectedIds.filter((id) =>
    currentTaskIds.has(id),
  ).length;
  const allChecked =
    currentTasks.length > 0 && selectedCurrentCount === currentTasks.length;
  const indeterminate =
    selectedCurrentCount > 0 && selectedCurrentCount < currentTasks.length;

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? currentTasks.map((t: TaskItem) => t.id) : []);
    },
    [currentTasks],
  );

  const handleSelectOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked
        ? prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((x) => x !== id),
    );
  }, []);

  const columns = [
    {
      title: (
        <Checkbox
          checked={allChecked}
          indeterminate={indeterminate}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'select',
      width: 44,
      render: (_: any, record: TaskItem) => (
        <Checkbox
          checked={selectedIds.includes(record.id)}
          onChange={(e) => handleSelectOne(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: '文件名称',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 180,
      render: (name: string, _record: TaskItem) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '4px 0',
          }}
        >
          <div
            className={styles.fileIcon}
            style={{ marginTop: 2, flexShrink: 0 }}
          >
            <FileImageOutlined style={{ transform: 'scale(0.8)' }} />
          </div>
          <Text
            style={{
              fontSize: 14,
              fontWeight: 400,
              wordBreak: 'break-all',
              lineHeight: '20px',
            }}
          >
            {name}
          </Text>
        </div>
      ),
    },
    {
      title: '域路径',
      dataIndex: 'domainPath',
      key: 'domainPath',
      width: 140,
      ellipsis: true,
      render: (path: string) => (
        <Text
          style={{ fontSize: 14, color: 'rgba(0,0,0,0.65)', fontWeight: 400 }}
        >
          {path}
        </Text>
      ),
    },
    {
      title: '文件大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 80,
      render: (size: number) => (
        <Text style={{ fontSize: 14, color: 'rgba(0,0,0,0.65)' }}>
          {formatSize(size, 2)}
        </Text>
      ),
    },
    ...(activeStatus === 'in-progress'
      ? [
          {
            title: '状态',
            key: 'status',
            render: (_: any, record: TaskItem) => {
              const percent = Math.round(
                (record.transferred / record.fileSize) * 100,
              );
              return (
                <div className={styles.statusCell}>
                  <div className={styles.progressWrapper}>
                    <Progress
                      percent={percent}
                      showInfo={false}
                      strokeColor={BLUE}
                      trailColor="#e8e8e8"
                      strokeWidth={4}
                      style={{
                        marginBottom: 0,
                        width: 120,
                        lineHeight: '13px',
                      }}
                    />
                    <div className={styles.speedRow}>
                      <Text
                        style={{
                          fontSize: 12,
                          color: 'rgba(0,0,0,0.45)',
                          lineHeight: '20px',
                        }}
                      >
                        {formatSize(record.transferred)}
                      </Text>
                      <Text
                        style={{
                          fontSize: 12,
                          color: 'rgba(0,0,0,0.45)',
                          lineHeight: '20px',
                        }}
                      >
                        {formatSpeed(record.speed)}
                      </Text>
                    </div>
                  </div>
                </div>
              );
            },
          },
        ]
      : [
          {
            title: '结果',
            dataIndex: 'status',
            key: 'result',
            width: 90,
            render: (_: unknown, record: TaskItem) => (
              <Text
                style={{
                  fontSize: 14,
                  color: record.status === 'failed' ? '#ff4d4f' : '#52c41a',
                }}
              >
                {record.status === 'failed' ? '失败' : '成功'}
              </Text>
            ),
          },
          {
            title: '完成时间',
            dataIndex: 'completedAt',
            key: 'completedAt',
            render: (time: string) => (
              <Text style={{ fontSize: 14, color: 'rgba(0,0,0,0.45)' }}>
                {time}
              </Text>
            ),
          },
        ]),
  ];

  return (
    <Modal
      open={open}
      closable={false}
      footer={null}
      width={900}
      mask={true}
      maskClosable={true}
      keyboard={true}
      onCancel={onClose}
      destroyOnHidden
      className={styles.modal}
      wrapClassName={styles.modal}
      style={{
        top: TASK_CENTER_TOP_OFFSET,
        right: 20,
        left: 'auto',
        margin: 0,
        padding: 0,
        position: 'fixed',
      }}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 20, fontWeight: 500, lineHeight: '26px' }}>
            任务中心
          </span>
          <CloseOutlined
            style={{
              fontSize: 14,
              color: 'rgba(0,0,0,0.45)',
              cursor: 'pointer',
            }}
            onClick={onClose}
          />
        </div>
      }
      styles={{
        header: {
          padding: '14px 20px',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: 0,
        },
        body: {
          padding: 0,
        },
        mask: {
          background: 'rgba(0,0,0,0.15)',
        },
      }}
    >
      <div className={styles.container}>
        <div className={styles.sidebar}>
          <div
            className={cx(
              styles.sidebarItem,
              activeDirection === 'upload' && styles.sidebarItemActive,
            )}
            onClick={() => {
              setActiveDirection('upload');
              setActiveStatus('in-progress');
              setSelectedIds([]);
            }}
          >
            上传
            {uploadCount > 0 && (
              <Badge
                count={uploadCount}
                size="small"
                style={{ backgroundColor: BLUE }}
              />
            )}
          </div>
          <div
            className={cx(
              styles.sidebarItem,
              activeDirection === 'download' && styles.sidebarItemActive,
            )}
            onClick={() => {
              setActiveDirection('download');
              setActiveStatus('in-progress');
              setSelectedIds([]);
            }}
          >
            下载
            {downloadCount > 0 && (
              <Badge
                count={downloadCount}
                size="small"
                style={{ backgroundColor: BLUE }}
              />
            )}
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.topRow}>
            <div className={styles.toolbar}>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={deleteBatch}
              >
                <DeleteOutlined /> 全部删除
              </button>
            </div>

            <Tabs
              className={styles.statusTabs}
              activeKey={activeStatus}
              onChange={(key) => {
                setActiveStatus(key as 'in-progress' | 'completed');
                setSelectedIds([]);
              }}
              items={[
                {
                  key: 'in-progress',
                  label: `${activeDirection === 'upload' ? '上传中' : '下载中'} (${inProgressTasks.length})`,
                },
                {
                  key: 'completed',
                  label: `已完成 (${completedTasks.length})`,
                },
              ]}
            />
          </div>

          <div className={styles.tableWrapper}>
            <Table<TaskItem>
              rowKey="id"
              columns={columns}
              dataSource={currentTasks}
              pagination={{
                defaultPageSize: TASK_PAGE_SIZE_DEFAULT,
                hideOnSinglePage: false,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20', '50'],
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条`,
              }}
              size="small"
              scroll={{ y: 300 }}
              locale={{ emptyText: '暂无任务' }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TaskCenterModal;
